import type { WorkspaceOperationNotice } from "./workspace-operations";

const MAX_RECORDS = 12;
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const COMMAND_KEY = /^[a-z][a-z0-9.-]{0,39}:[a-z0-9]+:[0-9a-f]{36}$/;
interface Policy {
  key: string;
  operation: RegExp;
}

interface SessionOperation {
  tenantId: string;
  accountId: string;
  operationType: string;
  idempotencyKey: string;
  startedAt: number;
}

interface SessionStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface Binding {
  tenantId: string;
  accountId: string;
}

function validRecord(
  value: unknown,
  now: number,
  policy: Policy,
): value is SessionOperation {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.tenantId === "string" &&
    UUID.test(row.tenantId) &&
    typeof row.accountId === "string" &&
    UUID.test(row.accountId) &&
    typeof row.operationType === "string" &&
    policy.operation.test(row.operationType) &&
    typeof row.idempotencyKey === "string" &&
    row.idempotencyKey.length <= 100 &&
    COMMAND_KEY.test(row.idempotencyKey) &&
    typeof row.startedAt === "number" &&
    Number.isFinite(row.startedAt) &&
    row.startedAt <= now
  );
}

function read(
  store: SessionStore,
  now: number,
  policy: Policy,
): SessionOperation[] {
  try {
    const parsed: unknown = JSON.parse(store.getItem(policy.key) ?? "[]");
    if (!Array.isArray(parsed)) throw new TypeError("Invalid operation store");
    return parsed
      .filter((row) => validRecord(row, now, policy))
      .slice(-MAX_RECORDS)
      .map(
        ({
          tenantId,
          accountId,
          operationType,
          idempotencyKey,
          startedAt,
        }) => ({
          tenantId,
          accountId,
          operationType,
          idempotencyKey,
          startedAt,
        }),
      );
  } catch {
    try {
      store.removeItem(policy.key);
    } catch {}
    return [];
  }
}

function write(store: SessionStore, rows: SessionOperation[], policy: Policy) {
  try {
    if (!rows.length) store.removeItem(policy.key);
    else store.setItem(policy.key, JSON.stringify(rows.slice(-MAX_RECORDS)));
  } catch {}
}

function restore(
  store: SessionStore,
  binding: Binding,
  now: number,
  policy: Policy,
): WorkspaceOperationNotice[] {
  const rows = read(store, now, policy);
  if (
    rows.some(
      (row) =>
        row.tenantId !== binding.tenantId ||
        row.accountId !== binding.accountId,
    )
  ) {
    clear(store, policy);
    return [];
  }
  write(store, rows, policy);
  return rows.map(({ operationType, idempotencyKey }) => ({
    operationType,
    idempotencyKey,
    status: "unknown",
  }));
}

function persist(
  store: SessionStore,
  binding: Binding,
  operationType: string,
  idempotencyKey: string,
  now: number,
  policy: Policy,
) {
  const candidate = {
    tenantId: binding.tenantId,
    accountId: binding.accountId,
    operationType,
    idempotencyKey,
    startedAt: now,
  };
  if (!validRecord(candidate, now, policy)) return;
  const rows = read(store, now, policy).filter(
    (row) => row.idempotencyKey !== idempotencyKey,
  );
  write(store, [...rows, candidate], policy);
}

function remove(
  store: SessionStore,
  idempotencyKey: string,
  now: number,
  policy: Policy,
) {
  write(
    store,
    read(store, now, policy).filter(
      (row) => row.idempotencyKey !== idempotencyKey,
    ),
    policy,
  );
}

function clear(store: SessionStore, policy: Policy) {
  try {
    store.removeItem(policy.key);
  } catch {}
}

export function createSessionOperationStore(policy: Policy) {
  return {
    restore: (store: SessionStore, binding: Binding, now = Date.now()) =>
      restore(store, binding, now, policy),
    persist: (
      store: SessionStore,
      binding: Binding,
      type: string,
      key: string,
      now = Date.now(),
    ) => persist(store, binding, type, key, now, policy),
    remove: (store: SessionStore, key: string, now = Date.now()) =>
      remove(store, key, now, policy),
    clear: (store: SessionStore) => clear(store, policy),
  };
}
